import { supabase } from '@/integrations/supabase/client';
import { ServiceError, withServiceErrorHandling } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import { Json } from '@/integrations/supabase/types';

interface NotificationInsert {
  user_id: string;
  organization_id: string;
  type: string;
  title: string;
  message: string;
  metadata?: Json;
}

export class NotificationsService {
  private static readonly TABLE = 'notifications';
  private static logger = logger.child({ component: 'NotificationsService' });

  /**
   * Create a notification for a user
   */
  static async create(notification: NotificationInsert): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Creating notification', { 
        userId: notification.user_id,
        type: notification.type 
      });

      const { error } = await supabase
        .from(this.TABLE)
        .insert(notification);

      if (error) {
        this.logger.error('Failed to create notification', error, { notification });
        throw error;
      }

      this.logger.info('Notification created successfully', { 
        userId: notification.user_id,
        type: notification.type 
      });
    }, { operation: 'create', table: this.TABLE });
  }

  /**
   * Create multiple notifications at once
   */
  static async createBatch(notifications: NotificationInsert[]): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Creating batch notifications', { 
        count: notifications.length 
      });

      const { error } = await supabase
        .from(this.TABLE)
        .insert(notifications);

      if (error) {
        this.logger.error('Failed to create batch notifications', error, { 
          count: notifications.length 
        });
        throw error;
      }

      this.logger.info('Batch notifications created successfully', { 
        count: notifications.length 
      });
    }, { operation: 'createBatch', table: this.TABLE });
  }

  /**
   * Get user's notifications
   */
  static async getForUser(userId: string, limit: number = 50) {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        this.logger.error('Failed to fetch notifications', error, { userId });
        throw error;
      }

      return data || [];
    }, { operation: 'getForUser', table: this.TABLE });
  }

  /**
   * Mark notification as read
   */
  static async markAsRead(notificationId: string): Promise<void> {
    return withServiceErrorHandling(async () => {
      const { error } = await supabase
        .from(this.TABLE)
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) {
        this.logger.error('Failed to mark notification as read', error, { 
          notificationId 
        });
        throw error;
      }

      this.logger.info('Notification marked as read', { notificationId });
    }, { operation: 'markAsRead', table: this.TABLE });
  }

  /**
   * Mark all user notifications as read
   */
  static async markAllAsRead(userId: string): Promise<void> {
    return withServiceErrorHandling(async () => {
      const { error } = await supabase
        .from(this.TABLE)
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .is('read_at', null);

      if (error) {
        this.logger.error('Failed to mark all notifications as read', error, { 
          userId 
        });
        throw error;
      }

      this.logger.info('All notifications marked as read', { userId });
    }, { operation: 'markAllAsRead', table: this.TABLE });
  }

  /**
   * Get or create notification preferences for a user
   */
  static async getPreferences(userId: string, organizationId: string) {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      // Create default preferences if none exist
      if (!data) {
        const { data: newPrefs, error: insertError } = await supabase
          .from('notification_preferences')
          .insert({ user_id: userId, organization_id: organizationId })
          .select()
          .maybeSingle();

        if (insertError) throw insertError;
        return newPrefs;
      }

      return data;
    }, { operation: 'getPreferences', table: 'notification_preferences' });
  }

  /**
   * Update notification preferences
   */
  static async updatePreferences(
    preferencesId: string, 
    updates: Record<string, boolean>
  ) {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from('notification_preferences')
        .update(updates)
        .eq('id', preferencesId)
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    }, { operation: 'updatePreferences', table: 'notification_preferences' });
  }
}
